import React, { useEffect, useState } from 'react';

import { Search, Upload, UserPlus } from 'lucide-react';
import { DateTime } from 'luxon';
import { queryCache, useMutation, useQuery } from 'react-query';
import { DotBricksLoading, Toast } from 'src/common/components';

import { archiveRecipientCohort, fetchRecipientCohortMembers, removeCohortMember } from '../api';
import {
  COHORT_MEMBERS_PAGE_SIZE,
  EDITION_AUDIENCE_LABEL,
  MEMBER_ORIGIN_LABEL,
  MEMBER_STATUS_CLASSES,
  MEMBER_STATUS_LABEL,
  RECIPIENT_COHORT_MEMBERS_QUERY_KEY,
  RECIPIENT_COHORT_PURPOSE_LABEL,
  RECIPIENT_COHORTS_QUERY_KEY,
} from '../constants';
import { MemberStatus, RecipientCohort } from '../types';
import { getCohortErrorMessage, invalidateCohortHistory } from '../utils';

import AddPersonModal from './AddPersonModal';
import CohortHistory from './CohortHistory';
import CsvImportModal from './CsvImportModal';
import RecipientCohortFormModal from './RecipientCohortFormModal';

type RecipientCohortDetailProps = {
  cohort: RecipientCohort;
  onArchived: () => void;
};

type View = 'members' | 'history';

const MEMBER_GRID = { gridTemplateColumns: '1.4fr 1fr 90px 130px 90px' };

const RecipientCohortDetail: React.FC<RecipientCohortDetailProps> = ({ cohort, onArchived }) => {
  const isBase = cohort.kind === 'base';
  const [view, setView] = useState<View>('members');
  const [status, setStatus] = useState<MemberStatus>('active');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showEdit, setShowEdit] = useState(false);
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [confirmingArchive, setConfirmingArchive] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery(
    [RECIPIENT_COHORT_MEMBERS_QUERY_KEY, cohort.id, debouncedSearch, status, page],
    () => fetchRecipientCohortMembers(cohort.id, debouncedSearch, status, page),
    { keepPreviousData: true }
  );

  const members = data?.data.results ?? [];
  const totalCount = data?.data.count ?? 0;
  const hasNextPage = page * COHORT_MEMBERS_PAGE_SIZE < totalCount;
  const lastPage = Math.max(1, Math.ceil(totalCount / COHORT_MEMBERS_PAGE_SIZE));

  useEffect(() => {
    if (data && page > lastPage) {
      setPage(lastPage);
    }
  }, [data, page, lastPage]);

  const [removeMember, { isLoading: isRemoving }] = useMutation(
    (userId: number) => removeCohortMember(cohort.id, userId),
    {
      onSuccess: () => {
        void queryCache.invalidateQueries([RECIPIENT_COHORT_MEMBERS_QUERY_KEY, cohort.id]);
        void queryCache.invalidateQueries(RECIPIENT_COHORTS_QUERY_KEY);
        invalidateCohortHistory('recipient', cohort.id);
        Toast.success('Persona quitada del cohort.');
      },
      onError: (error) => {
        Toast.error(getCohortErrorMessage(error, 'No se pudo quitar a la persona.'));
      },
    }
  );

  const [archive, { isLoading: isArchiving }] = useMutation(
    () => archiveRecipientCohort(cohort.id),
    {
      onSuccess: () => {
        void queryCache.invalidateQueries(RECIPIENT_COHORTS_QUERY_KEY);
        invalidateCohortHistory('recipient', cohort.id);
        Toast.success('Cohort archivado.');
        onArchived();
      },
      onError: (error) => {
        setConfirmingArchive(false);
        Toast.error(getCohortErrorMessage(error, 'No se pudo archivar el cohort.'));
      },
    }
  );

  const changeStatus = (next: MemberStatus): void => {
    setStatus(next);
    setPage(1);
  };

  const statusTabs: { id: MemberStatus; label: string }[] = [
    { id: 'active', label: `${MEMBER_STATUS_LABEL.active} · ${cohort.member_counts.active}` },
    {
      id: 'unsubscribed',
      label: `${MEMBER_STATUS_LABEL.unsubscribed} · ${cohort.member_counts.unsubscribed}`,
    },
    ...(isBase ? [] : [{ id: 'removed' as MemberStatus, label: MEMBER_STATUS_LABEL.removed }]),
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-shrink-0 px-6 py-4 bg-white border-b">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900 truncate">{cohort.name}</h2>
              {isBase && (
                <span className="px-2 py-0.5 text-xs font-semibold text-blue-700 bg-blue-50 rounded-full">
                  Base
                </span>
              )}
            </div>
            {cohort.description && (
              <p className="mt-1 text-sm text-gray-500">{cohort.description}</p>
            )}
            <p className="mt-1 text-xs text-gray-400">
              {cohort.purpose && `${RECIPIENT_COHORT_PURPOSE_LABEL[cohort.purpose]} · `}
              Lista de bajas: {EDITION_AUDIENCE_LABEL[cohort.send_list]}
            </p>
            {isBase && (
              <p className="mt-2 text-xs text-gray-500">
                Se calcula por rol y es de solo lectura: aquí puedes ver sus miembros y quién se dio
                de baja.
              </p>
            )}
          </div>
          {!isBase && (
            <div className="flex items-center flex-shrink-0 gap-2">
              <button
                type="button"
                className="btn btn-ghost inline-flex items-center gap-1 px-3 py-1.5 text-xs text-blue-700 border border-gray-200 rounded-full"
                onClick={() => setShowAddPerson(true)}
              >
                <UserPlus size={13} />
                Añadir persona
              </button>
              <button
                type="button"
                className="btn btn-ghost inline-flex items-center gap-1 px-3 py-1.5 text-xs text-blue-700 border border-gray-200 rounded-full"
                onClick={() => setShowImport(true)}
              >
                <Upload size={13} />
                Importar CSV
              </button>
              <button
                type="button"
                className="btn btn-ghost px-3 py-1.5 text-xs border border-gray-200 rounded-full"
                onClick={() => setShowEdit(true)}
              >
                Editar
              </button>
              {confirmingArchive ? (
                <span className="flex items-center gap-1 text-xs text-gray-600">
                  ¿Archivar?
                  <button
                    type="button"
                    className="btn btn-ghost px-2 py-1 text-xs text-red-600"
                    disabled={isArchiving}
                    onClick={() => archive()}
                  >
                    Sí, archivar
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost px-2 py-1 text-xs"
                    onClick={() => setConfirmingArchive(false)}
                  >
                    No
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className="btn btn-ghost px-3 py-1.5 text-xs text-red-600 border border-gray-200 rounded-full"
                  onClick={() => setConfirmingArchive(true)}
                >
                  Archivar
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center flex-shrink-0 gap-3 px-6 py-3 bg-white border-b">
        <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-full">
          {(
            [
              { id: 'members', label: 'Miembros' },
              { id: 'history', label: 'Historial' },
            ] as { id: View; label: string }[]
          ).map(({ id, label }) => (
            <button
              key={id}
              type="button"
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                view === id ? 'bg-white shadow text-gray-800' : 'text-gray-500'
              }`}
              onClick={() => setView(id)}
            >
              {label}
            </button>
          ))}
        </div>
        {view === 'members' && (
          <>
            <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-full">
              {statusTabs.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                    status === id ? 'bg-white shadow text-gray-800' : 'text-gray-500'
                  }`}
                  onClick={() => changeStatus(id)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="relative w-64 ml-auto">
              <Search
                size={14}
                className="left-3 top-1/2 absolute text-gray-400 -translate-y-1/2"
              />
              <input
                className="input pl-9 w-full text-sm"
                placeholder="Buscar por nombre, email o cuenta…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </>
        )}
      </div>

      <div className="flex-1 p-6 overflow-y-auto">
        {view === 'history' ? (
          <CohortHistory kind="recipient" cohortId={cohort.id} />
        ) : isLoading ? (
          <DotBricksLoading />
        ) : (
          <>
            {members.length === 0 ? (
              <p className="py-12 text-sm text-center text-gray-400">
                No hay personas en esta vista.
              </p>
            ) : (
              <div className="rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm">
                <div
                  className="bg-gray-50 grid gap-4 px-5 py-3 text-xs font-bold tracking-widest text-gray-400 uppercase border-b"
                  style={MEMBER_GRID}
                >
                  <div>Persona</div>
                  <div>Cuenta</div>
                  <div>Origen</div>
                  <div>Desde</div>
                  <div />
                </div>
                {members.map((member) => (
                  <div
                    key={member.user_id}
                    className="last:border-0 grid items-center gap-4 px-5 py-3 border-b border-gray-100"
                    style={MEMBER_GRID}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{member.name}</p>
                      <p className="text-xs text-gray-500 truncate">{member.email}</p>
                    </div>
                    <div className="text-sm text-gray-600 truncate">{member.account.name}</div>
                    <div className="flex flex-col items-start gap-1 text-xs text-gray-500">
                      {MEMBER_ORIGIN_LABEL[member.origin]}
                      {status !== 'active' && (
                        <span
                          className={`rounded-full px-2 py-0.5 font-semibold ${
                            MEMBER_STATUS_CLASSES[member.status]
                          }`}
                        >
                          {MEMBER_STATUS_LABEL[member.status]}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {member.status === 'removed' && member.removed_at
                        ? `Quitada ${DateTime.fromISO(member.removed_at).toLocaleString(
                            DateTime.DATE_MED
                          )}`
                        : member.added_at
                        ? DateTime.fromISO(member.added_at).toLocaleString(DateTime.DATE_MED)
                        : '—'}
                    </div>
                    <div className="text-right">
                      {!isBase && member.status !== 'removed' && (
                        <button
                          type="button"
                          className="btn btn-ghost disabled:opacity-50 px-3 py-1 text-xs text-red-600 border border-gray-200 rounded-full"
                          disabled={isRemoving}
                          onClick={() => removeMember(member.user_id)}
                        >
                          Quitar
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {totalCount > COHORT_MEMBERS_PAGE_SIZE && (
              <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                <button
                  type="button"
                  className="btn btn-ghost disabled:opacity-50 px-3 py-1 text-xs"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Anterior
                </button>
                <span>
                  Página {page} · {totalCount} personas
                </span>
                <button
                  type="button"
                  className="btn btn-ghost disabled:opacity-50 px-3 py-1 text-xs"
                  disabled={!hasNextPage}
                  onClick={() => setPage(page + 1)}
                >
                  Siguiente
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {showEdit && (
        <RecipientCohortFormModal
          cohort={cohort}
          onClose={() => setShowEdit(false)}
          onSaved={() => setShowEdit(false)}
        />
      )}
      {showAddPerson && <AddPersonModal cohort={cohort} onClose={() => setShowAddPerson(false)} />}
      {showImport && <CsvImportModal cohort={cohort} onClose={() => setShowImport(false)} />}
    </div>
  );
};

export default RecipientCohortDetail;
